import emailjs from '@emailjs/browser';

const PUBLIC_KEY = 'DUpSo3_mKDmSXYtwi'; // Reemplazar con tu public key de EmailJS
const TEMPLATE_ID = 'template_gc24f0h'; // Reemplazar con tu template ID de EmailJS
const SERVICE_ID = 'service_emv3a3a'; // Reemplazar con tu service ID de EmailJS
const ADMIN_TEMPLATE_ID = 'template_nblko5a'; // Template para notificación al administrador
const ADMIN_EMAIL = 'hosteriafutaleufu@gmail.com'; // Email del administrador

// Función para formatear la fecha al formato "día mes año"
const formatearFecha = (fecha) => {
  // Convertir la fecha a componentes para evitar problemas de zona horaria
  const [anio, mes, dia] = fecha.split('-').map(num => parseInt(num, 10));
  
  // Crear fecha con horas fijas (12:00) para evitar problemas de zona horaria
  const fechaObj = new Date(anio, mes - 1, dia, 12, 0, 0);
  
  const diaFormateado = fechaObj.getDate();
  const mesFormateado = fechaObj.toLocaleString('es-ES', { month: 'long' });
  const anioFormateado = fechaObj.getFullYear();
  
  return `${diaFormateado} de ${mesFormateado} de ${anioFormateado}`;
};

// Función para obtener el precio según la ocupación (igual que en FormularioReserva.js)
const obtenerPrecioPorOcupacion = (habitacion, ocupacion) => {
  // Convertir ocupación a número para asegurarnos de que las comparaciones sean correctas
  const ocupacionNum = parseInt(ocupacion) || 1;
  
  // Si la ocupación es mayor a la capacidad, usar la capacidad máxima
  const ocupacionAjustada = Math.min(ocupacionNum, habitacion.capacidad);
  
  // Si no tiene precios por ocupación o es un objeto vacío, usar el precio base
  if (!habitacion.preciosPorOcupacion || Object.keys(habitacion.preciosPorOcupacion).length === 0) {
    return habitacion.precio || 0;
  }
  
  // Obtener el precio según la ocupación o el precio para la capacidad máxima si no existe
  if (habitacion.preciosPorOcupacion[ocupacionAjustada]) {
    return parseInt(habitacion.preciosPorOcupacion[ocupacionAjustada]) || 0;
  }
  
  return habitacion.precio || 0;
};

// Verificar si estamos en entorno de desarrollo
const isDevelopmentEnvironment = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1';
};

/**
 * Envía un correo de confirmación para una reserva
 * @param {Object} reserva - Datos de la reserva
 * @param {boolean} esReservaPublica - Indica si la reserva viene del formulario público (true) o del admin (false)
 * @returns {Promise<boolean>} - Indica si el envío fue exitoso
 */
export const enviarCorreoConfirmacion = async (reserva, esReservaPublica = false) => {
  try {
    // Log detallado de la reserva
    console.log('Enviando correo para reserva:', JSON.stringify(reserva, null, 2), 
      'Es reserva pública:', esReservaPublica);
    
    // Asegurar que usamos la duración redondeada para el cálculo del precio total
    const duracionEstancia = Math.round(reserva.duracionEstancia);
    
    // Usar el precio total proporcionado por la reserva en lugar de recalcularlo
    // Esto asegura que se respeten los precios por temporada ya calculados
    const precioTotal = reserva.precioTotal;

    // Calcular el precio por noche dividiendo el precio total entre la duración
    const precioPorNoche = Math.round(precioTotal / duracionEstancia);
    
    // Crear el detalle de habitaciones con su distribución de personas
    const detalleHabitaciones = reserva.habitaciones.map(h => {
      const personas = reserva.distribucionPersonas[h.id] || 1;
      // Usar nombre o name, o un fallback si ninguno está presente
      const nombreHabitacion = h.nombre || h.name || `Habitación ${h.id}`;
      return `${nombreHabitacion} (${personas} ${personas === 1 ? 'persona' : 'personas'})`;
    }).join(', ');
    
    // Parámetros para el correo del cliente
    const templateParams = {
      to_name: reserva.nombre,
      to_email: reserva.email,
      check_in: formatearFecha(reserva.fechaEntrada),
      check_out: formatearFecha(reserva.fechaSalida),
      habitaciones: detalleHabitaciones,
      duracion_estancia: duracionEstancia,
      precio_por_noche: precioPorNoche,
      total: precioTotal,
      codigo_reserva: reserva.codigoReserva
    };

    console.log('Parámetros de la plantilla:', templateParams);
    console.log('Enviando correo con EmailJS:', { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY });

    // Asegurar que emailjs está inicializado correctamente
    if (!emailjs.init) {
      console.log('Inicializando EmailJS...');
      emailjs.init(PUBLIC_KEY);
    }

    // Enviar correo al cliente
    const clientResponse = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
    console.log('Respuesta de EmailJS (cliente):', clientResponse);
    
    // Solo enviar correo al administrador si la reserva viene del formulario público
    if (esReservaPublica) {
      console.log('Enviando correo de notificación al administrador');
      
      // Parámetros para el correo del administrador
      const adminTemplateParams = {
        admin_email: ADMIN_EMAIL,
        cliente_nombre: reserva.nombre,
        cliente_email: reserva.email,
        cliente_telefono: reserva.telefono || "No especificado",
        check_in: formatearFecha(reserva.fechaEntrada),
        check_out: formatearFecha(reserva.fechaSalida),
        habitaciones: detalleHabitaciones,
        personas: Object.values(reserva.distribucionPersonas).reduce((sum, val) => sum + val, 0),
        duracion_estancia: duracionEstancia,
        precio_por_noche: precioPorNoche,
        total: precioTotal,
        codigo_reserva: reserva.codigoReserva,
        fecha_creacion: new Date().toLocaleString()
      };
      
      // Enviar correo al administrador
      const adminResponse = await emailjs.send(SERVICE_ID, ADMIN_TEMPLATE_ID, adminTemplateParams, PUBLIC_KEY);
      console.log('Respuesta de EmailJS (admin):', adminResponse);
    } else {
      console.log('Omitiendo envío de correo al administrador porque la reserva se creó desde el panel admin');
    }
    
    // Si estamos en desarrollo local, mostrar un mensaje adicional
    if (isDevelopmentEnvironment()) {
      console.log('NOTA: En entorno de desarrollo, es posible que los correos no lleguen debido a restricciones de CORS o bloqueos de red local.');
      console.log('Simularemos que el correo se envió correctamente para fines de prueba.');
      
      // Simular alerta para entorno local
      if (window.alert) {
        window.alert(`[DESARROLLO] Se simuló el envío de correo a ${templateParams.to_email}`);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error al enviar el correo:', error);
    
    // Mostrar detalles del error para ayudar en la depuración
    if (error.text) console.error('Mensaje de error:', error.text);
    if (error.status) console.error('Estado HTTP:', error.status);
    if (error.name) console.error('Nombre del error:', error.name);
    if (error.message) console.error('Mensaje completo:', error.message);
    
    // En entorno de desarrollo, permitir continuar como si el correo se hubiera enviado
    if (isDevelopmentEnvironment()) {
      console.log('DESARROLLO: Simulando éxito a pesar del error para fines de prueba.');
      return true;
    }
    
    return false;
  }
}; 